import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import SectionOrchestrator from './SectionOrchestrator';
import PRDGenerationButtons from '../PRDGenerationButtons';
import { normalizeSectionName } from '../../../utils/sectionColors';

/**
 * Convert string to sentence case, preserving special terms like UI/UX, API, CI/CD
 * @param {string} str - String to convert
 * @returns {string} String in sentence case with special terms preserved
 */
const toSentenceCase = (str) => {
  if (!str) return '';

  // First normalize special terms (UI/UX, API, CI/CD, etc.)
  const normalized = normalizeSectionName(str);

  // Check for UI/UX pattern directly (case-insensitive)
  const lowerStr = str.toLowerCase().trim();
  if (lowerStr === 'ui/ux' || lowerStr.includes('ui/ux')) {
    return 'UI/UX';
  }

  // If normalization changed the string, return the normalized version
  if (normalized !== str) {
    return normalized;
  }

  // Otherwise apply standard sentence case
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Component to display section header with title and orchestrator
 * @param {Object} props - Component props
 * @param {string} props.sectionName - Name of the section
 * @param {string} props.sectionId - ID of the section
 * @param {string} props.projectId - ID of the project
 * @param {Object} props.consolidatedAvatar - Avatar data from consolidated API
 * @param {function} props.onOrchestratorUpdate - Callback to update orchestrator cache
 * @returns {React.ReactElement} Section header component
 */
const SectionHeader = ({
  sectionName,
  sectionId,
  projectId,
  consolidatedAvatar,
  onOrchestratorUpdate
}) => {
  // Initialize with null values - only use what comes from API
  const [sectionAvatar, setSectionAvatar] = useState({
    avatarUrl: null,
    avatarName: null
  });

  // Use consolidated data from props
  useEffect(() => {
    if (consolidatedAvatar) {
      console.log('consolidatedAvatar', consolidatedAvatar);
      setSectionAvatar({
        avatarUrl: consolidatedAvatar.url,
        avatarName: consolidatedAvatar.name
      });
    }
  }, [consolidatedAvatar]);

  return (
    <Box
      sx={{
        width: { xs: '100%', md: '200px' },
        minWidth: { md: '200px' },
        position: 'relative',
        pt: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: { xs: 'center', md: 'flex-start' }
      }}
    >
      {/* Vertical divider line on right */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '1px',
          bgcolor: 'rgba(0, 0, 0, 0.12)',
          display: { xs: 'none', md: 'block' }
        }}
      />

      <Box sx={{
        mb: 0.5,
        textAlign: { xs: 'center', md: 'left' },
        width: '100%',
        display: 'flex',
        flexDirection: "row",
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontSize: { xs: '1.1rem', md: '1.2rem' },
            fontWeight: 600,
            color: '#333',
            pb: 0.5,
            pl: { xs: 0 },
            pr: { md: 1 }
          }}
        >
          {toSentenceCase(sectionName)}
        </Typography>

        {/* Show PRD generation buttons only on the Foundation section, positioned to the left of "+" icon */}
        {sectionName === 'Foundation' && projectId && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 2
          }}>
            <PRDGenerationButtons projectId={projectId} />
          </Box>
        )}
      </Box>

      {/* Section Orchestrator in new style */}
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: { xs: 'center', md: 'flex-start' },
        pr: { md: 3 },
        ml: { md: 0 },
        mt: { xs: 1, md: 2 }
      }}>
        <SectionOrchestrator
          sectionId={sectionId}
          sectionName={toSentenceCase(sectionName)}
          avatarUrl={sectionAvatar.avatarUrl}
          avatarName={sectionAvatar.avatarName}
          projectId={projectId}
          onOrchestratorUpdate={onOrchestratorUpdate}
        />
      </Box>
    </Box>
  );
};

SectionHeader.propTypes = {
  sectionName: PropTypes.string.isRequired,
  sectionId: PropTypes.string,
  projectId: PropTypes.string,
  consolidatedAvatar: PropTypes.object,
  onOrchestratorUpdate: PropTypes.func
};

export default React.memo(SectionHeader);
